import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from '@floating-ui/dom'
import { unref } from 'vue'

class TooltipClass {
  /**
   * Dom object
   * @type HTMLElement
   */
  el

  /**
   * @type HTMLElement
   */
  tooltip

  /**
   * @type HTMLElement
   */
  tooltipContent

  /**
   * @type HTMLElement
   */
  tooltipArrow

  /**
   * List of default options
   * @type {{placement: string, type: string, content: string}}
   */
  defaultOptions = {
    content: 'Tooltip',
    placement: 'bottom',
    type: 'default',
    show: false,
    debug: false,
    referenceEl: null,
  }

  /**
   * Tooltip options state
   */
  options = { ...this.defaultOptions }

  cleanupAutoUpdate = null

  hideTimer = null

  isTriggerHovered = false

  isTooltipHovered = false

  triggerListeners = {}

  tooltipListeners = {}

  createTooltip() {
    this.tooltip = document.createElement('div')
    this.tooltip.classList.add('tooltip')

    this.tooltipContent = document.createElement('div')
    this.tooltipContent.classList.add('tooltip-content')

    this.tooltipArrow = document.createElement('div')
    this.tooltipArrow.classList.add('tooltip-arrow')

    this.tooltipContent.append(this.tooltipArrow)
    this.tooltip.append(this.tooltipContent)
  }

  static getRootNode() {
    const id = 'tooltip-root'
    let root = document.getElementById(id)

    if (!root) {
      root = document.createElement('div')
      root.setAttribute('id', id)
      root.style.position = 'fixed'
      root.style.left = '0'
      root.style.top = '0'
      root.style.width = '0'
      root.style.height = '0'
      root.style.zIndex = '120'
      root.style.pointerEvents = 'none'
      document.body.append(root)
    }

    return root
  }

  static toFloatingPlacement(placement) {
    const [basePlacement, align] = (placement || 'bottom').split('-')

    if (!align || align === 'center') {
      return basePlacement
    }

    if (['start', 'end'].includes(align)) {
      return `${basePlacement}-${align}`
    }

    return basePlacement
  }

  isShown() {
    return !!(
      this.options.show ||
      this.isTriggerHovered ||
      this.isTooltipHovered
    )
  }

  /**
   * Update options
   * @param bindings
   */
  setOptions(bindings) {
    let params = {}

    if (bindings.arg) {
      params[bindings.arg] = bindings.value
    } else if (typeof bindings.value === 'string') {
      params.content = bindings.value
    } else {
      params = bindings.value || {}
    }

    for (let key in this.defaultOptions) {
      if (Object.hasOwnProperty.call(params, key)) {
        this.options[key] = unref(params[key])
      }
    }
  }

  setContent(content) {
    this.tooltipContent.innerHTML = content
    this.tooltipContent.append(this.tooltipArrow)
  }

  clearAutoUpdate() {
    if (typeof this.cleanupAutoUpdate === 'function') {
      this.cleanupAutoUpdate()
      this.cleanupAutoUpdate = null
    }
  }

  getReferenceEl() {
    return this.options.referenceEl || this.el
  }

  async calcPosition() {
    if (!this.tooltip.isConnected) {
      return
    }

    const referenceEl = this.getReferenceEl()

    if (!referenceEl?.isConnected) {
      this.tooltip.remove()
      this.clearAutoUpdate()
      return
    }

    const placement = TooltipClass.toFloatingPlacement(this.options.placement)
    const data = await computePosition(referenceEl, this.tooltip, {
      placement,
      strategy: 'fixed',
      middleware: [
        offset(0),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
        arrow({ element: this.tooltipArrow, padding: 8 }),
      ],
    })

    const staticSide = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[data.placement.split('-')[0]]

    this.tooltip.style.left = `${data.x}px`
    this.tooltip.style.top = `${data.y}px`
    this.tooltipArrow.style.left = ''
    this.tooltipArrow.style.top = ''
    this.tooltipArrow.style.right = ''
    this.tooltipArrow.style.bottom = ''

    if (data.middlewareData.arrow) {
      const { x, y } = data.middlewareData.arrow

      if (typeof x === 'number') {
        this.tooltipArrow.style.left = `${x}px`
      }

      if (typeof y === 'number') {
        this.tooltipArrow.style.top = `${y}px`
      }
    }

    if (staticSide) {
      this.tooltipArrow.style[staticSide] = '-6px'
    }
  }

  render() {
    let attrs = ['placement', 'type']
    for (let attr of attrs) {
      this.tooltip.setAttribute('data-' + attr, this.options[attr])
    }

    this.setContent(this.options.content)
    this.tooltip.classList.toggle('debug', this.options.debug)

    if (this.isShown()) {
      const root = TooltipClass.getRootNode()
      if (!this.tooltip.isConnected) {
        root.append(this.tooltip)
      }

      this.calcPosition()
      this.clearAutoUpdate()
      this.cleanupAutoUpdate = autoUpdate(
        this.getReferenceEl(),
        this.tooltip,
        () => {
          this.calcPosition()
        },
      )
    } else {
      this.tooltip.remove()
      this.clearAutoUpdate()
    }
  }

  onTriggerEnter() {
    this.isTriggerHovered = true
    clearTimeout(this.hideTimer)
    this.render()
  }

  onTriggerLeave() {
    this.isTriggerHovered = false
    this.hideTimer = setTimeout(() => this.render(), 60)
  }

  onTooltipEnter() {
    this.isTooltipHovered = true
    clearTimeout(this.hideTimer)
    this.render()
  }

  onTooltipLeave() {
    this.isTooltipHovered = false
    this.hideTimer = setTimeout(() => this.render(), 60)
  }

  click(event) {
    event.stopPropagation()
  }

  constructor(el, bindings) {
    this.el = el
    this.createTooltip()

    this.setOptions(bindings)
    this.render()
    this.bindTooltipEvents()
  }

  bindTriggerEvents() {
    this.triggerListeners = {
      mouseenter: this.onTriggerEnter.bind(this),
      mouseleave: this.onTriggerLeave.bind(this),
      click: this.click.bind(this),
    }

    this.el.addEventListener('mouseenter', this.triggerListeners.mouseenter)
    this.el.addEventListener('mouseleave', this.triggerListeners.mouseleave)
    this.el.addEventListener('click', this.triggerListeners.click)
  }

  bindTooltipEvents() {
    this.tooltipListeners = {
      mouseenter: this.onTooltipEnter.bind(this),
      mouseleave: this.onTooltipLeave.bind(this),
    }

    this.tooltip.addEventListener(
      'mouseenter',
      this.tooltipListeners.mouseenter,
    )
    this.tooltip.addEventListener(
      'mouseleave',
      this.tooltipListeners.mouseleave,
    )
  }

  destroy() {
    clearTimeout(this.hideTimer)
    this.clearAutoUpdate()
    this.tooltip.remove()

    if (this.triggerListeners.mouseenter) {
      this.el.removeEventListener(
        'mouseenter',
        this.triggerListeners.mouseenter,
      )
    }

    if (this.triggerListeners.mouseleave) {
      this.el.removeEventListener(
        'mouseleave',
        this.triggerListeners.mouseleave,
      )
    }

    if (this.triggerListeners.click) {
      this.el.removeEventListener('click', this.triggerListeners.click)
    }

    if (this.tooltipListeners.mouseenter) {
      this.tooltip.removeEventListener(
        'mouseenter',
        this.tooltipListeners.mouseenter,
      )
    }

    if (this.tooltipListeners.mouseleave) {
      this.tooltip.removeEventListener(
        'mouseleave',
        this.tooltipListeners.mouseleave,
      )
    }
  }
}

export const vTooltip = {
  /**
   * Mount tooltip
   *
   * @param el
   * @param bindings
   */
  mounted(el, bindings) {
    if (!el.tooltip) {
      el.tooltip = new TooltipClass(el, bindings)
      el.tooltip.bindTriggerEvents()
    } else {
      el.tooltip.setOptions(bindings)
      el.tooltip.render()
    }
  },

  /**
   * Only update tooltip contains
   *
   * @param el
   * @param bindings
   */
  updated(el, bindings) {
    el.tooltip.setOptions(bindings)
    el.tooltip.render()
  },

  /**
   * Remove element
   * @param el
   */
  beforeUnmount(el) {
    if (el.tooltip) {
      el.tooltip.destroy()
      delete el.tooltip
    }
  },
}
